import { Variable } from '@/types/variables';
import { Icon } from '@iconify/react';
import {
  Avatar,
  Button,
  Chip,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ScrollShadow,
} from '@nextui-org/react';
import { useMemo } from 'react';

interface UserEntry {
  user_id: number;
  name: string;
  value?: string;
  source: string;
  type: string;
  updated_at?: string | null;
  captured_by_agent_name?: string | null;
  props: {
    source?: string;
    [key: string]: any;
  };
  is_private: boolean;
  required: boolean;
}

interface UserValuesPanelProps {
  isOpen: boolean;
  onClose: () => void;
  user: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  } | null;
  userEntries: UserEntry[];
  variableDefinitions: Variable[];
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const day = d.getDate();
  const month = d.toLocaleString('en-GB', { month: 'short' });
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const mins = String(d.getMinutes()).padStart(2, '0');
  return `${day} ${month} ${year}, ${hours}:${mins}`;
}

function getSourceBadge(source?: string, agentName?: string | null) {
  if (source === 'user_profile') {
    return (
      <Chip size="sm" variant="flat" color="primary">
        Profile
      </Chip>
    );
  }
  if (source === 'conversation') {
    return (
      <Chip
        size="sm"
        variant="flat"
        classNames={{
          base: 'bg-purple-100',
          content: 'text-purple-600',
        }}
      >
        {agentName || 'Conversation'}
      </Chip>
    );
  }
  return null;
}

export function UserValuesPanel({
  isOpen,
  onClose,
  user,
  userEntries,
  variableDefinitions,
}: UserValuesPanelProps) {
  // Compute derived data — safe to call even when user is null
  const { withValues, withoutValues } = useMemo(() => {
    const wv: Array<{ def: Variable; entry: UserEntry }> = [];
    const wov: Variable[] = [];
    if (!user) return { withValues: wv, withoutValues: wov };

    const entriesByName = new Map<string, UserEntry>();
    userEntries.forEach((entry) => {
      entriesByName.set(entry.name, entry);
    });

    variableDefinitions.forEach((def) => {
      const entry = entriesByName.get(def.name);
      if (
        entry &&
        entry.value !== null &&
        entry.value !== undefined &&
        entry.value !== ''
      ) {
        wv.push({ def, entry });
      } else {
        wov.push(def);
      }
    });

    return { withValues: wv, withoutValues: wov };
  }, [user, userEntries, variableDefinitions]);

  const initials = user
    ? (user.first_name?.[0] || '') + (user.last_name?.[0] || '')
    : '';
  const fullName = user ? `${user.first_name} ${user.last_name}`.trim() : '';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      hideCloseButton
      classNames={{
        wrapper: '!items-start !justify-end',
        base: '!m-0 rounded-medium h-[calc(100vh-64px)] w-full sm:w-[500px] max-w-[35vw]',
        backdrop: 'bg-black/20',
      }}
      motionProps={{
        variants: {
          enter: {
            x: 0,
            transition: { duration: 0.3 },
          },
          exit: {
            x: 500,
            transition: { duration: 0.2, ease: 'easeIn' },
          },
        },
      }}
    >
      <ModalContent className="absolute top-2 right-2 h-[calc(100vh-20px)]">
        {(onModalClose) =>
          user && (
            <>
              <ModalHeader className="flex justify-between items-start px-4 py-3 border-b">
                <div className="flex flex-col">
                  <span className="text-large font-semibold">
                    User variables
                  </span>
                  <span className="text-tiny text-foreground-400 font-normal">
                    Stored values for this user across all variable definitions
                  </span>
                </div>
                <Button isIconOnly variant="light" onPress={onModalClose}>
                  <Icon icon="mdi:close" className="h-5 w-5" />
                </Button>
              </ModalHeader>

              <ModalBody className="overflow-hidden p-0">
                <ScrollShadow className="h-[calc(100vh-200px)]">
                  {/* User info */}
                  <div className="flex items-center gap-3 px-5 py-4 border-b border-default-200">
                    <Avatar
                      name={initials}
                      size="md"
                      classNames={{
                        base: 'bg-primary/10 text-primary',
                        name: 'text-sm font-semibold',
                      }}
                    />
                    <div className="flex flex-col">
                      <span className="text-base font-semibold">
                        {fullName}
                      </span>
                      <span className="text-sm text-foreground-400">
                        {user.email}
                      </span>
                    </div>
                  </div>

                  {/* Variable cards */}
                  <div className="px-5 py-2">
                    {withValues.map(({ def, entry }) => (
                      <div
                        key={def.name}
                        className="py-4 border-b border-dashed border-default-200 last:border-b-0"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-base font-bold font-mono">
                            {def.name}
                          </span>
                          {getSourceBadge(
                            def.props?.source,
                            entry.captured_by_agent_name
                          )}
                        </div>
                        <div className="bg-default-50 border-l-3 border-l-default-300 rounded-r-lg px-4 py-3 mb-2">
                          <span className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
                            {entry.value}
                          </span>
                        </div>
                        {entry.updated_at && (
                          <div className="text-tiny text-foreground-400">
                            Updated {formatDate(entry.updated_at)}
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Variables without values */}
                    {withoutValues.length > 0 && (
                      <>
                        <div className="pt-4 pb-2">
                          <span className="text-xs text-foreground-400 uppercase tracking-wide font-medium">
                            No value captured
                          </span>
                        </div>
                        {withoutValues.map((def) => (
                          <div
                            key={def.name}
                            className="py-4 border-b border-dashed border-default-200 last:border-b-0"
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-base font-bold font-mono">
                                {def.name}
                              </span>
                              {getSourceBadge(def.props?.source)}
                            </div>
                            <div className="bg-default-50 border-l-3 border-l-default-200 rounded-r-lg px-4 py-3">
                              <span className="text-sm text-foreground-300 italic">
                                Empty
                              </span>
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                </ScrollShadow>
              </ModalBody>

              <div className="flex justify-end p-4 border-t">
                <Button variant="bordered" onPress={onModalClose}>
                  Close
                </Button>
              </div>
            </>
          )
        }
      </ModalContent>
    </Modal>
  );
}
